/**
 * [PURPOSE]: Sub-plan 2A — generic typed picker. Wraps the kind→catalog
 *   dispatch of composerCatalogRegistry. Single-mode renders a labeled
 *   <select>; multi-mode renders chips + an "append" select.
 *
 * [WHY]: Replaces every "select from a hardcoded inline list" pattern in
 *   the codebase. Sub-plan 2B rollout wires existing free-text fields to
 *   this picker (race languages, race choose-features, custom-class
 *   spellcasting ability, feat prereqs, item ammo type, spell scaling).
 *
 * [API]:
 *   <ReferencePicker
 *     kind="skill"                // see composerCatalogRegistry.listKnownKinds()
 *     value={null}                // single: string | null; multi: string[]
 *     onChange={(next) => void}   // next is string (single) or string[] (multi)
 *     multi={false}               // true → chips array UI
 *     allowMint={false}           // shows + button to create a new vocab entry
 *     playerStats={null}          // forwarded to registry for SRD+custom kinds
 *     placeholder="-- Choose --"  // dropdown placeholder
 *     disabled={false}
 *     chipVariant="emerald"       // [2E] semantic chip color: emerald | red | amber | purple | blue
 *     valueShape="prefixed"       // [2E] 'prefixed' keeps 'kind:id'; 'unprefixed' strips to bare 'id'
 *   />
 *
 * [VISUAL]: Matches the rest of the Workshop authoring console — emerald
 *   accents on interactive surfaces, slate body, amber on metadata. Multi-mode
 *   chip color can be themed per consumer via `chipVariant` so semantic
 *   classes (resistances=red, immunities=amber, vulnerabilities=purple) read
 *   at-a-glance.
 *
 * [SUB-PLAN 2E — OPTION-A USAGE PATTERN]: For consumers that store an
 *   *object array* keyed by stable id (e.g., CustomFeatCreator addAdvantage
 *   pushes `{ id: nanoid(), target }`), do NOT try to make ReferencePicker
 *   emit the wrapped shape. Instead, use ReferencePicker as the DROPDOWN HALF
 *   only and keep the chip-rendering loop in the consumer:
 *
 *     // dropdown — single-mode picker with no current value, fires onChange
 *     // with the picked id, then consumer wraps it
 *     <ReferencePicker
 *       kind="advantageTarget"
 *       value={null}
 *       onChange={(targetId) => addAdvantage(targetId)}  // consumer wraps
 *     />
 *     // chips — render manually from the consumer's wrapped array
 *     {grantedAdvantages.map((a) => <Chip key={a.id} ... />)}
 *
 *   This avoids a `valueWrapper` prop with two value contracts on one component
 *   (the alternative would force every consumer to consider whether the picker
 *   emits strings or objects, doubling the API surface). Recommendation per
 *   Wave-2 ingenuity finding #3.
 */

import React, { useMemo, useState } from 'react';
import { getCatalog } from './composerCatalogRegistry';

// ============================================================================
// [SUB-PLAN 2E] chip color variants
// ============================================================================
// Per Wave-2 ingenuity finding #1 — multi-mode chips were hardcoded emerald.
// Semantic colors: resistances=red, immunities=amber, vulnerabilities=purple,
// conditions=blue (and the default emerald for "grant" surfaces).
//
// Each variant pairs the chip span tokens + the matching remove-button text
// tokens so hover state stays consistent. Unknown variant → fall back to
// emerald (defensive — never render an unstyled chip).
const CHIP_VARIANT_CLASSES = {
  emerald: {
    chip: 'bg-emerald-900/30 text-emerald-300 border-emerald-700/40',
    remove: 'text-emerald-400 hover:text-emerald-200',
  },
  red: {
    chip: 'bg-red-900/30 text-red-300 border-red-700/40',
    remove: 'text-red-400 hover:text-red-200',
  },
  amber: {
    chip: 'bg-amber-900/30 text-amber-300 border-amber-700/40',
    remove: 'text-amber-400 hover:text-amber-200',
  },
  purple: {
    chip: 'bg-purple-900/30 text-purple-300 border-purple-700/40',
    remove: 'text-purple-400 hover:text-purple-200',
  },
  blue: {
    chip: 'bg-blue-900/30 text-blue-300 border-blue-700/40',
    remove: 'text-blue-400 hover:text-blue-200',
  },
};

// [SUB-PLAN 2E] Strip the 'kind:' prefix from a catalog id (e.g.,
// 'skill:athletics' → 'athletics'). Ids without a colon pass through. Used
// only when valueShape='unprefixed' so consumers that store bare ids can
// swap to the picker without inline `.replace('kind:', '')` adapters.
function stripKindPrefix(id) {
  if (typeof id !== 'string') return id;
  const idx = id.indexOf(':');
  return idx >= 0 ? id.slice(idx + 1) : id;
}

function ReferencePicker({
  kind,
  value,
  onChange,
  multi = false,
  allowMint = false,
  playerStats = null,
  placeholder = '-- Choose --',
  disabled = false,
  chipVariant = 'emerald',
  valueShape = 'prefixed',
  // [SUB-PLAN 1H-H-cond Phase 2]: forwarded to the inner <select> so label
  //   htmlFor associations work when ReferencePicker replaces a plain <input>.
  id,
}) {
  const rawCatalog = useMemo(() => getCatalog(kind, playerStats), [kind, playerStats]);

  // [SUB-PLAN 2E] When valueShape='unprefixed', surface a transformed catalog
  // whose ids are bare (no 'kind:' prefix). Everything downstream — option
  // values, value comparison, chip rendering, onChange emission — uses this
  // displayCatalog, so the round trip is uniform without per-call-site adapters.
  const catalog = useMemo(() => {
    if (valueShape !== 'unprefixed') return rawCatalog;
    return rawCatalog.map((entry) => ({ ...entry, id: stripKindPrefix(entry.id) }));
  }, [rawCatalog, valueShape]);

  // [SUB-PLAN 2E] Resolve chip variant once. Unknown variant → emerald fallback.
  const variant = CHIP_VARIANT_CLASSES[chipVariant] || CHIP_VARIANT_CLASSES.emerald;

  const [mintOpen, setMintOpen] = useState(false);
  const [mintText, setMintText] = useState('');

  // Graceful degradation: unknown kind → render nothing.
  // (Exception: if allowMint=true we still want to show the mint surface for
  // ad-hoc vocabularies, but the picker is currently only used with known
  // kinds; tests assert null for unknown.)
  if (catalog.length === 0) {
    return null;
  }

  const handleMintSubmit = () => {
    const next = mintText.trim();
    if (!next) return;
    if (multi) {
      const arr = Array.isArray(value) ? value : [];
      if (!arr.includes(next)) {
        onChange && onChange([...arr, next]);
      }
    } else {
      onChange && onChange(next);
    }
    setMintText('');
    setMintOpen(false);
  };

  const renderMintBlock = () => {
    if (!allowMint) return null;
    return (
      <>
        <button
          type="button"
          data-testid="reference-picker-mint"
          onClick={() => setMintOpen(true)}
          className="px-2 py-1 bg-emerald-900/40 text-emerald-300 border border-emerald-700/40 rounded text-xs hover:bg-emerald-900/60"
          title="Add a new entry"
          disabled={disabled}
        >
          +
        </button>
        {mintOpen && (
          <>
            <input
              type="text"
              data-testid="reference-picker-mint-input"
              value={mintText}
              onChange={(e) => setMintText(e.target.value)}
              placeholder="New entry…"
              className="px-2 py-1 bg-gray-800 text-white rounded border border-emerald-700/40 outline-none focus:border-emerald-500 text-xs"
              autoFocus
            />
            <button
              type="button"
              data-testid="reference-picker-mint-submit"
              onClick={handleMintSubmit}
              className="px-2 py-1 bg-emerald-700 text-white rounded text-xs hover:bg-emerald-600"
            >
              Add
            </button>
          </>
        )}
      </>
    );
  };

  // ============================================================================
  // Single-mode
  // ============================================================================
  if (!multi) {
    // [STONE 1 — 2026-05-25]: Optgroup support. When entries carry meta.group,
    // group them into <optgroup> blocks. This is additive / backward-compatible:
    // entries without meta.group render flat as before (no regression for existing
    // kinds). Detection: at least one entry with a truthy meta.group triggers
    // grouped rendering; all entries for that kind must carry meta.group.
    //
    // [STONE A — 2026-06-05]: preserve an out-of-vocab CURRENT value as a
    // visible, selected option. Without this, a stored value not in the catalog
    // (genuine prose authored via mint, e.g. monster alignment "Neutral Good
    // (50%) or Neutral Evil (50%)") renders BLANK — a broken hybrid (the DM
    // can't see what's stored, and a mint vanishes on reload). Gated on
    // allowMint so closed-vocab consumers are 100% unaffected. Single-mode only
    // (multi-mode already renders unknown ids as chips at L264).
    const valueInCatalog = !value || catalog.some((e) => e.id === value);
    const effectiveCatalog = (allowMint && value && !valueInCatalog)
      ? [...catalog, { id: value, label: value }]
      : catalog;
    const hasGroups = effectiveCatalog.some((e) => e.meta && e.meta.group);

    // Build optgroup buckets when grouped. Preserve insertion order by using a
    // Map (unlike a plain object whose key-insertion order may vary in old engines).
    let groupedOptions = null;
    if (hasGroups) {
      const groupMap = new Map();
      for (const entry of effectiveCatalog) {
        const groupKey = (entry.meta && entry.meta.group) || '';
        if (!groupMap.has(groupKey)) groupMap.set(groupKey, []);
        groupMap.get(groupKey).push(entry);
      }
      groupedOptions = groupMap;
    }

    return (
      <div
        data-testid="reference-picker"
        className="inline-flex items-center gap-2 flex-wrap"
      >
        <select
          data-testid="reference-picker-select"
          id={id}
          value={value || ''}
          onChange={(e) => onChange && onChange(e.target.value)}
          disabled={disabled}
          className="px-2 py-1 bg-gray-800 text-white rounded border border-gray-700 focus:border-emerald-500 outline-none text-sm disabled:opacity-50"
        >
          <option value="">{placeholder}</option>
          {hasGroups
            ? Array.from(groupedOptions.entries()).map(([groupName, entries]) => (
                <optgroup key={groupName} label={groupName}>
                  {entries.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.label}
                    </option>
                  ))}
                </optgroup>
              ))
            : effectiveCatalog.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.label}
                </option>
              ))}
        </select>
        {renderMintBlock()}
      </div>
    );
  }

  // ============================================================================
  // Multi-mode
  // ============================================================================
  const arr = Array.isArray(value) ? value : [];
  const availableForAdd = catalog.filter((entry) => !arr.includes(entry.id));

  return (
    <div data-testid="reference-picker" className="flex flex-col gap-2">
      {arr.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {arr.map((id) => {
            const entry = catalog.find((e) => e.id === id);
            const label = entry ? entry.label : id;
            return (
              <span
                key={id}
                data-testid={`reference-picker-chip-${id}`}
                className={`inline-flex items-center gap-1 px-2 py-0.5 ${variant.chip} rounded text-xs`}
              >
                {label}
                <button
                  type="button"
                  data-testid={`reference-picker-remove-${id}`}
                  onClick={() => onChange && onChange(arr.filter((x) => x !== id))}
                  className={`${variant.remove} ml-1`}
                  aria-label={`Remove ${label}`}
                  disabled={disabled}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}
      <div className="inline-flex items-center gap-2 flex-wrap">
        <select
          data-testid="reference-picker-select"
          value=""
          onChange={(e) => {
            const next = e.target.value;
            if (next && !arr.includes(next)) {
              onChange && onChange([...arr, next]);
            }
          }}
          disabled={disabled}
          className="px-2 py-1 bg-gray-800 text-white rounded border border-gray-700 focus:border-emerald-500 outline-none text-sm disabled:opacity-50"
        >
          <option value="">{placeholder}</option>
          {availableForAdd.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.label}
            </option>
          ))}
        </select>
        {renderMintBlock()}
      </div>
    </div>
  );
}

export default ReferencePicker;
